import { useMemo, useState } from 'react';
import { Pagination, Select, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { LoadingOutlined } from '@ant-design/icons';
import { TrackingData, RecipientRow } from '../types';
import styles from './mailing-tracking-view.module.scss';
import warningSvg from './warning.svg';
import cartSvg from './cart-shopping-solid.svg';
import qrSvg from './qrcode-solid.svg';
import arrowSvg from './arrow-pointer-solid.svg';

interface Props {
  data: TrackingData | null;
  loading?: boolean;
  error?: string | null;
  totalPiecesOrdered?: number | null;
  qrCodesScanned?: number | null;
  informedDeliveryClicks?: number | null;
}

const sortIcon = ({ sortOrder }: { sortOrder: SortOrder }) => (
  <FontAwesomeIcon
    icon={sortOrder === 'ascend' ? faSortUp : sortOrder === 'descend' ? faSortDown : faSort}
    style={{ marginLeft: 4, fontSize: 11, opacity: sortOrder ? 1 : 0.35 }}
  />
);

const STATUS_TOOLTIPS: Record<string, string> = {
  'In Processing':        'Order is in production at our facility. Future scheduled orders will also be in this status.',
  'In Transit':           'Production is complete and mail has left the facility for processing by USPS.',
  'In Mailstream':        'Mail that has received first scan from USPS tracking systems.',
  'In Destination State': 'Your mail has reached the destination state and is being processed for local delivery.',
  'Out for Delivery':     'Your mail is out for delivery and expected to reach the recipient shortly.',
  'Delivered':            'Mail has been successfully delivered to the recipient.',
  'Relabeled':            'Indicates mail was forwarded to a new address. Sometimes signifies a return to sender.',
  'Marked for Return':    'USPS has determined the mail is undeliverable and is returning it to the sender\'s address.',
  'Cancelled by USPS':    'USPS has cancelled this mail piece and it will not be delivered.',
};

const DARK_ROWS       = new Set(['Relabeled', 'Marked for Return', 'Cancelled by USPS']);
const REDIRECTED_ROWS = new Set(['Relabeled']);

export const formatMailDate = (dateTime?: string | null): { date: string | null; time: string | null } => {
  if (!dateTime) return { date: null, time: null };
  const [datePart, timePart] = dateTime.split(' ');
  if (!datePart) return { date: null, time: null };
  const [year, month, day] = datePart.split('-');
  const date = `${month}-${day}-${year}`;
  const hour = parseInt(timePart?.slice(0, 2) ?? '0', 10);
  const rest  = timePart?.slice(2, 5) ?? ':00';
  const time  = hour > 12
    ? `${String(hour - 12).padStart(2, '0')}${rest} PM`
    : `${timePart?.slice(0, 5) ?? '00:00'} AM`;
  return { date, time };
};

const DateCell = ({ value }: { value: string | null }) => {
  const { date, time } = formatMailDate(value);
  if (!date) return null;
  return (
    <>
      <div className={styles.cellDate}>{date}</div>
      <div className={styles.cellTime}>{time}</div>
    </>
  );
};

const col = <T extends RecipientRow & { key: string }>(c: ColumnType<T>): ColumnType<T> => ({
  ...c,
  sortIcon,
});

const RECIPIENT_COLUMNS: ColumnsType<RecipientRow & { key: string }> = [
  col({
    title: 'RECIPIENT NAME',
    dataIndex: 'recipientName',
    key: 'recipientName',
    width: 150,
    sorter: (a, b) => (a.recipientName ?? '').localeCompare(b.recipientName ?? ''),
    defaultSortOrder: 'ascend',
  }),
  col({
    title: 'RECIPIENT ADDRESS',
    dataIndex: 'recipientAddress',
    key: 'recipientAddress',
    width: 202,
    sorter: (a, b) => (a.recipientAddress ?? '').localeCompare(b.recipientAddress ?? ''),
    render: (val: string) => {
      if (!val) return null;
      // Support both newline-delimited (legacy) and comma-separated (API) formats
      if (val.includes('\n')) {
        const [line1, ...rest] = val.split('\n');
        const line2 = rest.join(' ');
        return (
          <>
            <div className={styles.addressL1}>{line1}</div>
            {line2 && <div className={styles.addressL2}>{line2}</div>}
          </>
        );
      }
      const commaIdx = val.indexOf(',');
      const line1 = commaIdx !== -1 ? val.slice(0, commaIdx) : val;
      const line2 = commaIdx !== -1 ? val.slice(commaIdx + 2) : '';
      return (
        <>
          <div className={styles.addressL1}>{line1}</div>
          {line2 && <div className={styles.addressL2}>{line2}</div>}
        </>
      );
    },
  }),
  col({
    title: 'QR SCAN',
    dataIndex: 'qrScan',
    key: 'qrScan',
    width: 106,
    sorter: (a, b) => (a.qrScan ?? '').localeCompare(b.qrScan ?? ''),
    render: (val: string | null) => <DateCell value={val} />,
  }),
  col({
    title: (
      <>
        ID CLICK TIME
        <Tooltip title="Indicates the time recipient clicked on the Informed Delivery email">
          <span className={styles.infoIcon}>i</span>
        </Tooltip>
      </>
    ),
    dataIndex: 'idClickTime',
    key: 'idClickTime',
    width: 140,
    sorter: (a, b) => (a.idClickTime ?? '').localeCompare(b.idClickTime ?? ''),
    render: (val: string | null) => <DateCell value={val} />,
  }),
  col({
    title: 'STATUS',
    dataIndex: 'status',
    key: 'status',
    width: 83,
    sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
    render: (_val: string, record) => record.trackingStatus ?? 'In Processing',
  }),
  col({
    title: 'LAST UPDATE',
    dataIndex: 'lastUpdate',
    key: 'lastUpdate',
    width: 106,
    sorter: (a, b) => (a.lastUpdate ?? '').localeCompare(b.lastUpdate ?? ''),
    render: (val: string | null) => <DateCell value={val} />,
  }),
  col({
    title: 'INTELLIGENT MAIL BARCODE',
    dataIndex: 'intelligentMailBarcode',
    key: 'intelligentMailBarcode',
    width: 189,
    sorter: (a, b) => (a.intelligentMailBarcode ?? '').localeCompare(b.intelligentMailBarcode ?? ''),
  }),
];

const MailingTrackingView: React.FC<Props> = ({ data, loading, error, totalPiecesOrdered, qrCodesScanned, informedDeliveryClicks }) => {
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'ascend' | 'descend' }>({
    field: 'recipientName',
    order: 'ascend',
  });

  const summaryRows    = data?.statusSummary?.filter(f => f.status !== 'Delivered') ?? [];
  const allRecipients  = useMemo(() => data?.recipients ?? [], [data]);
  const hasData        = !loading && !error && !!data?.statusSummary;

  const sortedRecipients = useMemo(() => {
    const field = sortConfig.field as keyof RecipientRow;
    return [...allRecipients].sort((a, b) => {
      const av = String(a[field] ?? '');
      const bv = String(b[field] ?? '');
      const cmp = av.localeCompare(bv);
      return sortConfig.order === 'ascend' ? cmp : -cmp;
    });
  }, [allRecipients, sortConfig]);

  const pagedRecipients = useMemo(
    () => sortedRecipients
      .slice((page - 1) * pageSize, page * pageSize)
      .map((r, i) => ({ ...r, key: String(i) })),
    [sortedRecipients, page, pageSize],
  );

  const totalRecipients = data?.totalRecipients ?? allRecipients.length;

  const columnsWithSort: ColumnsType<RecipientRow & { key: string }> = RECIPIENT_COLUMNS.map(c => ({
    ...c,
    sortOrder: (c.key === sortConfig.field ? sortConfig.order : null) as SortOrder,
  }));

  return (
    <div className={styles.mailingTracking}>
      {/* ── Warning banner ── */}
      {!loading && data?.warning && (
        <div className={styles.warningBanner}>
          <div className={styles.warningIcon}>
            <img src={warningSvg as string} alt="Warning" />
          </div>
          {data.warning}
        </div>
      )}

      {/* ── Body ── */}
      {loading ? (
        <div className={styles.middleMessage}>
          <Spin indicator={<LoadingOutlined spin />} />
          <span>Loading...</span>
        </div>
      ) : (error || !data?.statusSummary) ? (
        <div className={styles.middleMessage}>
          {error ?? 'Mailing info unavailable. Please try again later.'}
        </div>
      ) : (
        <div className={styles.body}>
          {/* ── Tracing Summary ── */}
          <div className={styles.tracingSummary}>
            <div className={styles.summaryLeft}>
              {([
                { label: 'TOTAL PIECES ORDERED',     qty: totalPiecesOrdered ?? data.totalPiecesOrdered,          icon: cartSvg,  cls: styles.cartIcon  },
                { label: 'QR CODES SCANNED',         qty: (qrCodesScanned ?? data.qrCodesScanned) ?? 0,           icon: qrSvg,    cls: styles.qrIcon    },
                { label: 'INFORMED DELIVERY CLICKS', qty: (informedDeliveryClicks ?? data.informedDeliveryClicks) ?? 0, icon: arrowSvg, cls: styles.clickIcon },
              ] as const).map(({ label, qty, icon, cls }) => (
                <div key={label} className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon}>
                    <img className={cls} src={icon as string} alt={label} />
                  </div>
                  <div className={styles.summaryCardInfo}>
                    <div className={styles.summaryCardLabel}>{label}</div>
                    <div className={styles.summaryCardQty}>
                      {(qty as number)?.toLocaleString('en-US') ?? 0}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.summaryRight}>
              <table className={styles.summaryTable}>
                <thead>
                  <tr>
                    <th>STATUS</th>
                    <th>AMOUNT OF PIECES</th>
                    <th>% OF MAILED</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => (
                    <tr
                      key={row.status}
                      className={[
                        DARK_ROWS.has(row.status)       ? styles.darkRow       : '',
                        REDIRECTED_ROWS.has(row.status) ? styles.redirectedRow : '',
                      ].join(' ')}
                    >
                      <td>
                        {row.status}
                        {STATUS_TOOLTIPS[row.status] && (
                          <Tooltip title={STATUS_TOOLTIPS[row.status]}>
                            <span className={styles.infoIcon}>i</span>
                          </Tooltip>
                        )}
                      </td>
                      <td>{row.amountOfPieces?.toLocaleString('en-US')}</td>
                      <td>{row.percentOfMailed != null ? `${row.percentOfMailed.toFixed(1)}%` : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Recipient Table ── */}
          {!data.expired && (
            <div className={styles.recipientTable}>
              <Table<RecipientRow & { key: string }>
                dataSource={pagedRecipients}
                columns={columnsWithSort}
                size="small"
                showSorterTooltip={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'Mailing info unavailable. Please try again later.' }}
                pagination={false}
                onChange={(_, __, sorter) => {
                  const s = Array.isArray(sorter) ? sorter[0] : sorter;
                  if (s.columnKey && s.order) {
                    setSortConfig({ field: String(s.columnKey), order: s.order });
                  } else {
                    setSortConfig({ field: 'recipientName', order: 'ascend' });
                  }
                  setPage(1);
                }}
              />
              {totalRecipients > pageSize && (
                <div className={styles.paginationFooter}>
                  <div className={styles.paginationSizer}>
                    <Select
                      value={pageSize}
                      onChange={(val) => { setPageSize(val); setPage(1); }}
                      showSearch={false}
                      options={[10, 25, 50].map(n => ({ value: n, label: n }))}
                      size="small"
                    />
                    <span className={styles.paginationLabel}>Results per Page</span>
                  </div>
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={totalRecipients}
                    onChange={setPage}
                    showSizeChanger={false}
                    size="small"
                  />
                  <span className={styles.paginationTotal}>
                    {`${Math.min((page - 1) * pageSize + 1, totalRecipients).toLocaleString('en-US')}–${Math.min(page * pageSize, totalRecipients).toLocaleString('en-US')} of ${totalRecipients.toLocaleString('en-US')}`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Expired bubble ── */}
      {!loading && data?.expired && hasData && (
        <div className={styles.expiredBubble}>
          <div className={styles.expiredNote}>
            <img src={warningSvg as string} alt="Warning" />
            Detailed USPS mail tracking data is expired but here is the summary of your mail
          </div>
        </div>
      )}
    </div>
  );
};

export default MailingTrackingView;
